import { UserAvatar } from '@/components/user-avatar'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { memo, useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import type { ColumnDef } from '@/libs/table'
import { actionsColumnBase, stickyNameClass, stickyNameCellClass } from '@/components/table-styles'
import type { ColumnMeta } from '@/components/table-styles'
import type { ApprovalStage, TimeOffRequest, TimeOffType } from '@/features/hr/domain/timeoff'
import { getApprovalStage } from './get-approval-stage'

type TimeOffTableMeta = {
	onView?: (id: string) => void
}

const stageVariant: Record<ApprovalStage, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	pending_dept: 'secondary',
	pending_hr: 'secondary',
	approved: 'default',
	rejected: 'destructive',
}

const MemberCell = memo(({ name, avatarUrl }: { name: string; avatarUrl: string | null }) => (
	<div className='flex items-center space-x-2'>
		<UserAvatar name={name} avatarUrl={avatarUrl} size='xs' />
		<span className='truncate'>{name}</span>
	</div>
))
MemberCell.displayName = 'MemberCell'

const TypeCell = memo(({ type }: { type: TimeOffType }) => {
	const content = useIntlayer('hr-page')
	return <span className='text-muted-foreground text-sm'>{content.timeOff.requestType[type]?.value ?? type}</span>
})
TypeCell.displayName = 'TypeCell'

const StageCell = memo(({ request }: { request: TimeOffRequest }) => {
	const content = useIntlayer('hr-page')
	const stage = getApprovalStage(request)
	const label = content.timeOff.stage[stage]?.value ?? stage
	return <Badge variant={stageVariant[stage]}>{label}</Badge>
})
StageCell.displayName = 'StageCell'

const DatesCell = memo(({ request }: { request: TimeOffRequest }) => {
	const { formatPreferenceDate } = useDateFormat()
	const single = request.startDate === request.endDate
	return (
		<span className='truncate'>
			{formatPreferenceDate(request.startDate)}
			{!single && ` - ${formatPreferenceDate(request.endDate)}`}
			{request.halfDay && ` (${request.halfDay.toUpperCase()})`}
		</span>
	)
})
DatesCell.displayName = 'DatesCell'

const ActionsCell = memo(({ request, meta }: { request: TimeOffRequest; meta: TimeOffTableMeta | undefined }) => {
	const content = useIntlayer('hr-page')
	return (
		<div className='flex items-center justify-center w-full'>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant='ghost' className='h-8 w-8 p-0'>
						<Icons.MoreHoriz size={16} />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end'>
					<DropdownMenuItem onClick={() => meta?.onView?.(request.id)}>{content.actions.view}</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
})
ActionsCell.displayName = 'ActionsCell'

const memberColumn = (
	c: ReturnType<typeof useIntlayer<'hr-page'>>['timeOff']['columns'],
	avatarByMemberId: Map<string, string>,
): ColumnDef<TimeOffRequest, unknown> => ({
	id: 'member',
	accessorKey: 'memberName',
	header: c.member.value,
	size: 240,
	minSize: 180,
	enableResizing: true,
	meta: {
		headerLabel: c.member.value,
		sticky: true,
		headerClassName: `w-[240px] min-w-[180px] md:sticky md:left-0 bg-background z-10 ${stickyNameClass}`,
		className: `w-[240px] min-w-[180px] md:sticky md:left-0 bg-background group-hover:bg-hover-group  z-10 ${stickyNameCellClass}`,
	} satisfies ColumnMeta,
	cell: ({ row }) => (
		<MemberCell name={row.original.memberName} avatarUrl={avatarByMemberId.get(row.original.memberId) ?? null} />
	),
})

const sharedColumns = (
	c: ReturnType<typeof useIntlayer<'hr-page'>>['timeOff']['columns'],
): ColumnDef<TimeOffRequest, unknown>[] => [
	{
		id: 'type',
		accessorKey: 'type',
		header: c.type.value,
		size: 360,
		minSize: 200,
		enableResizing: true,
		meta: { headerLabel: c.type.value } satisfies ColumnMeta,
		cell: ({ row }) => <TypeCell type={row.original.type} />,
	},
	{
		id: 'dates',
		accessorKey: 'startDate',
		header: c.dates.value,
		size: 240,
		minSize: 200,
		enableResizing: true,
		meta: { headerLabel: c.dates.value } satisfies ColumnMeta,
		cell: ({ row }) => <DatesCell request={row.original} />,
	},
	{
		id: 'status',
		accessorKey: 'deptApproval',
		header: c.status.value,
		size: 200,
		minSize: 110,
		enableResizing: true,
		meta: { headerLabel: c.status.value } satisfies ColumnMeta,
		cell: ({ row }) => <StageCell request={row.original} />,
	},
	{
		id: 'approver',
		accessorKey: 'deptApproval',
		header: c.approver.value,
		size: 180,
		minSize: 110,
		enableResizing: true,
		meta: { headerLabel: c.approver.value } satisfies ColumnMeta,
		cell: ({ row }) => {
			const r = row.original
			const actor = r.deptApproval.actorName ?? r.hrApproval.actorName
			return <span className='text-muted-foreground'>{actor ?? '-'}</span>
		},
	},
]

const actionsColumn = (
	c: ReturnType<typeof useIntlayer<'hr-page'>>['timeOff']['columns'],
): ColumnDef<TimeOffRequest, unknown> => ({
	...actionsColumnBase,
	header: c.actions.value,
	meta: { ...actionsColumnBase.meta, headerLabel: c.actions.value },
	cell: ({ row, table }) => (
		<ActionsCell request={row.original} meta={table.options.meta as TimeOffTableMeta | undefined} />
	),
})

const useTimeOffColumns = (avatarByMemberId: Map<string, string>) => {
	const content = useIntlayer('hr-page')
	const c = content.timeOff.columns
	return useMemo<ColumnDef<TimeOffRequest, unknown>[]>(
		() => [memberColumn(c, avatarByMemberId), ...sharedColumns(c), actionsColumn(c)],
		[c, avatarByMemberId],
	)
}

const useTimeOffOwnColumns = () => {
	const content = useIntlayer('hr-page')
	const c = content.timeOff.columns
	return useMemo<ColumnDef<TimeOffRequest, unknown>[]>(() => [...sharedColumns(c), actionsColumn(c)], [c])
}

export { useTimeOffColumns, useTimeOffOwnColumns }
export type { TimeOffTableMeta }
